import React, { useState } from 'react';
import { User, Edit3, Calendar, Crown, Flame, Play, Clock, Heart, Trophy, Bell, Shield, HelpCircle, ChevronRight, LogOut, Phone, MapPin, Cake, Target, Upload, Camera, Check, X, Loader } from 'lucide-react';

// ✅ Import centralized theme
import { gradients, buttonStyles, cardStyles, avatarStyles } from '../shared/config/theme';

const ProfilePage = ({ 
  user, 
  userProfile, 
  userProgress, 
  userName, 
  userEmail,
  setUserName,
  setShowEditProfile,
  showEditProfile,
  updateProfile,
  upgradeToPremium,
  handleLogout,
  setNotification 
}) => {
  const profileData = userProfile || {};
  const progressData = userProgress || { total_sessions: 0, mindful_minutes: 0, current_streak: 0, longest_streak: 0 };

  // ✅ Local notification + saving state inside the modal
  const [localNotification, setLocalNotification] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const showLocalNotification = (message, type = 'success') => {
    setLocalNotification({ message, type });
    setTimeout(() => setLocalNotification(null), 3000);
  };

  // ✅ New state for additional profile fields
  const [profilePhoto, setProfilePhoto] = useState(profileData.profile_photo || '');
  const [phoneNumber, setPhoneNumber] = useState(profileData.phone_number || '');
  const [gender, setGender] = useState(profileData.gender || '');
  const [dateOfBirth, setDateOfBirth] = useState(profileData.date_of_birth || '');
  const [focusArea, setFocusArea] = useState(profileData.focus_area || '');
  const [timeZone, setTimeZone] = useState(profileData.time_zone || Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [photoPreview, setPhotoPreview] = useState(profileData.profile_photo || '');

  const focusAreas = [
    { value: 'Confidence', emoji: '💪' },
    { value: 'Love', emoji: '❤️' },
    { value: 'Abundance', emoji: '✨' },
    { value: 'Healing', emoji: '🌙' },
    { value: 'Gratitude', emoji: '🙏' },
    { value: 'Focus', emoji: '🎯' },
    { value: 'Clarity', emoji: '💎' },
    { value: 'Joy', emoji: '☀️' },
    { value: 'Courage', emoji: '🦁' }
  ];

  const genderOptions = ['Male', 'Female', 'Non-binary', 'Prefer not to say', 'Other'];

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
        setProfilePhoto(reader.result); // In production, upload to storage and save URL
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpdateProfile = async () => {
    if (!userName || !userName.trim()) {
      showLocalNotification('Please enter your name', 'error');
      return;
    }

    const updatedData = {
      full_name: userName,
      profile_photo: profilePhoto,
      phone_number: phoneNumber,
      gender: gender,
      date_of_birth: dateOfBirth,
      focus_area: focusArea,
      time_zone: timeZone
    };

    try {
      setIsSaving(true);
      await updateProfile(updatedData);
      // ✅ Show success directly in modal - don't rely on parent
      showLocalNotification('Profile updated successfully! ✓', 'success');
      // Close modal after short delay so user sees the success message
      setTimeout(() => {
        setShowEditProfile(false);
      }, 1500);
    } catch (error) {
      showLocalNotification('Failed to save. Please try again.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const getUserInitial = () => {
    const name = profileData.full_name || user?.user_metadata?.full_name || userName || 'User';
    return name.charAt(0).toUpperCase();
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br ${gradients.background} pb-24`}>
      {/* Edit Profile Modal */}
      {showEditProfile && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`${cardStyles.default.background} rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl`}>
            <div className="sticky top-0 bg-white border-b border-peach-200 p-6 rounded-t-3xl">
              <div className="flex items-center justify-between">
                <h3 className="text-2xl font-bold text-gray-800">Edit Profile</h3>
                <button 
                  onClick={() => setShowEditProfile(false)} 
                  className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
                >
                  <span className="text-gray-600 text-xl">✕</span>
                </button>
              </div>
              {/* ✅ Inline notification inside modal header */}
              {localNotification && (
                <div className={`mt-3 px-4 py-2.5 rounded-xl flex items-center gap-2 text-sm font-medium ${
                  localNotification.type === 'success'
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  {localNotification.type === 'success'
                    ? <Check className="w-4 h-4 flex-shrink-0" />
                    : <X className="w-4 h-4 flex-shrink-0" />
                  }
                  {localNotification.message}
                </div>
              )}
            </div>
            
            <div className="p-6 space-y-6">
              {/* Profile Photo Upload */}
              <div className="flex flex-col items-center gap-4">
                <div className="relative">
                  {photoPreview ? (
                    <img 
                      src={photoPreview} 
                      alt="Profile" 
                      className="w-24 h-24 rounded-full object-cover border-4 border-peach-200"
                    />
                  ) : (
                    <div className={`w-24 h-24 bg-gradient-to-br from-orange-400 to-orange-500 rounded-full flex items-center justify-center text-white font-bold text-3xl border-4 border-peach-200`}>
                      {getUserInitial()}
                    </div>
                  )}
                  <label className="absolute bottom-0 right-0 w-8 h-8 bg-white rounded-full flex items-center justify-center cursor-pointer shadow-lg border-2 border-peach-200 hover:bg-peach-50 transition-colors">
                    <Camera className="w-4 h-4 text-orange-600" />
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={handlePhotoUpload}
                      className="hidden"
                    />
                  </label>
                </div>
                <p className="text-sm text-gray-500">Click camera icon to upload photo</p>
              </div>

              {/* Basic Info */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Name</label>
                  <input
                    type="text"
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                    placeholder="Enter your name"
                    className="w-full px-4 py-3 bg-peach-50 border border-peach-200 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                  <input
                    type="email"
                    value={user?.email || userEmail}
                    disabled
                    className="w-full px-4 py-3 bg-gray-100 border border-gray-200 rounded-xl text-gray-500 cursor-not-allowed"
                  />
                  <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    Phone Number <span className="text-gray-400 text-xs">(Optional)</span>
                  </label>
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="+1 (555) 000-0000"
                    className="w-full px-4 py-3 bg-peach-50 border border-peach-200 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">For account recovery or SMS reminders</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Gender <span className="text-gray-400 text-xs">(Optional)</span>
                  </label>
                  <select
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                    className="w-full px-4 py-3 bg-peach-50 border border-peach-200 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                  >
                    <option value="">Select gender</option>
                    {genderOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Helps personalize your experience</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <Cake className="w-4 h-4" />
                    Date of Birth <span className="text-gray-400 text-xs">(Optional)</span>
                  </label>
                  <input
                    type="date"
                    value={dateOfBirth}
                    onChange={(e) => setDateOfBirth(e.target.value)}
                    className="w-full px-4 py-3 bg-peach-50 border border-peach-200 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">For birthday wishes and insights</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <Target className="w-4 h-4" />
                    Primary Focus Area
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {focusAreas.map(area => (
                      <button
                        key={area.value}
                        onClick={() => setFocusArea(area.value)}
                        className={`px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                          focusArea === area.value
                            ? 'bg-gradient-to-r from-orange-400 to-orange-500 text-white shadow-md'
                            : 'bg-white border border-peach-200 text-gray-700 hover:bg-peach-50'
                        }`}
                      >
                        <span className="mr-1">{area.emoji}</span>
                        {area.value}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Helps customize affirmations and content</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Time Zone
                  </label>
                  <select
                    value={timeZone}
                    onChange={(e) => setTimeZone(e.target.value)}
                    className="w-full px-4 py-3 bg-peach-50 border border-peach-200 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                  >
                    <option value="America/New_York">Eastern Time (ET)</option>
                    <option value="America/Chicago">Central Time (CT)</option>
                    <option value="America/Denver">Mountain Time (MT)</option>
                    <option value="America/Los_Angeles">Pacific Time (PT)</option>
                    <option value="America/Phoenix">Arizona (MST)</option>
                    <option value="Pacific/Honolulu">Hawaii (HST)</option>
                    <option value="America/Anchorage">Alaska (AKST)</option>
                    <option value="Europe/London">London (GMT)</option>
                    <option value="Europe/Paris">Paris (CET)</option>
                    <option value="Asia/Dubai">Dubai (GST)</option>
                    <option value="Asia/Kolkata">India (IST)</option>
                    <option value="Asia/Singapore">Singapore (SGT)</option>
                    <option value="Asia/Tokyo">Tokyo (JST)</option>
                    <option value="Australia/Sydney">Sydney (AEDT)</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">For scheduling reminders and meditation</p>
                </div>
              </div>
            </div>
            
            <div className="sticky bottom-0 bg-white border-t border-peach-200 p-6 rounded-b-3xl flex gap-3">
              <button 
                onClick={() => setShowEditProfile(false)} 
                className={`flex-1 ${buttonStyles.secondary.base} ${buttonStyles.secondary.hover} ${buttonStyles.secondary.active} ${buttonStyles.secondary.transition} ${buttonStyles.secondary.rounded} py-3 px-6`}
              >
                Cancel
              </button>
              <button 
                onClick={handleUpdateProfile}
                disabled={isSaving}
                className={`flex-1 ${buttonStyles.primary.base} ${buttonStyles.primary.hover} ${buttonStyles.primary.active} ${buttonStyles.primary.transition} ${buttonStyles.primary.rounded} ${buttonStyles.primary.shadow} py-3 px-6 flex items-center justify-center gap-2 disabled:opacity-70`}
              >
                {isSaving ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-peach-200/50 sticky top-0 z-10 shadow-sm">
        <div className="px-6 py-4 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-800">Profile</h1>
          <button 
            onClick={() => setShowEditProfile(true)} 
            className="flex items-center gap-2 bg-peach-100 hover:bg-peach-200 text-orange-600 font-medium px-4 py-2 rounded-full transition-all duration-300"
          >
            <Edit3 size={18} />
            <span className="hidden sm:inline">Edit</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        {/* User Info Card */}
        <div className={`${cardStyles.default.background} ${cardStyles.default.rounded} ${cardStyles.default.shadow} ${cardStyles.default.border} ${cardStyles.default.padding}`}>
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
            <div className="relative">
              {profileData.profile_photo ? (
                <img 
                  src={profileData.profile_photo} 
                  alt="Profile" 
                  className="w-20 h-20 rounded-full object-cover border-4 border-peach-200 shadow-lg"
                />
              ) : (
                <div className={`w-20 h-20 bg-gradient-to-br from-orange-400 to-orange-500 rounded-full flex items-center justify-center text-white font-bold text-2xl border-4 border-peach-200 shadow-lg`}>
                  {getUserInitial()}
                </div>
              )}
            </div>

            <div className="flex-1 text-center sm:text-left">
              <h2 className="text-2xl font-bold text-gray-800 mb-1">
                {profileData.full_name || user?.user_metadata?.full_name || userName || 'User'}
              </h2>
              <p className="text-gray-600 mb-3">{user?.email || userEmail}</p>
              
              <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
                {profileData.is_premium && (
                  <div className="flex items-center gap-1.5 bg-gradient-to-r from-orange-400 to-orange-500 text-white px-3 py-1 rounded-full text-sm font-semibold shadow-sm">
                    <Crown size={14} />
                    <span>Premium</span>
                  </div>
                )}
                <div className="flex items-center gap-1.5 bg-peach-100 text-orange-700 px-3 py-1 rounded-full text-sm font-medium">
                  <Calendar size={14} />
                  <span>Joined {new Date(user?.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</span>
                </div>
                {profileData.focus_area && (
                  <div className="flex items-center gap-1.5 bg-cream-100 text-brown-700 px-3 py-1 rounded-full text-sm font-medium">
                    <Target size={14} />
                    <span>{profileData.focus_area}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Streak Card */}
            <div className="bg-gradient-to-br from-orange-50 to-peach-50 border border-orange-200 rounded-2xl p-4 min-w-[200px]">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-orange-500 rounded-full flex items-center justify-center">
                  <Flame className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-orange-600">{progressData.current_streak}</h3>
                  <p className="text-sm text-orange-700">Day Streak</p>
                </div>
              </div>
              <div className="pt-3 border-t border-orange-200">
                <p className="text-xs text-gray-600 mb-1">Best Streak</p>
                <p className="text-lg font-bold text-gray-800">{progressData.longest_streak} days</p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className={`${cardStyles.default.background} ${cardStyles.default.rounded} ${cardStyles.default.shadow} ${cardStyles.default.border} p-4`}>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl flex items-center justify-center">
                <Play className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-800">{progressData.total_sessions}</h3>
                <p className="text-sm text-gray-600">Total Sessions</p>
              </div>
            </div>
          </div>

          <div className={`${cardStyles.default.background} ${cardStyles.default.rounded} ${cardStyles.default.shadow} ${cardStyles.default.border} p-4`}>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-green-200 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-800">{progressData.mindful_minutes}</h3>
                <p className="text-sm text-gray-600">Mindful Minutes</p>
              </div>
            </div>
          </div>

          <div className={`${cardStyles.default.background} ${cardStyles.default.rounded} ${cardStyles.default.shadow} ${cardStyles.default.border} p-4`}>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-pink-100 to-pink-200 rounded-xl flex items-center justify-center">
                <Heart className="w-6 h-6 text-pink-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-800">{progressData.favorite_category || 'Confidence'}</h3>
                <p className="text-sm text-gray-600">Favorite</p>
              </div>
            </div>
          </div>

          <div className={`${cardStyles.default.background} ${cardStyles.default.rounded} ${cardStyles.default.shadow} ${cardStyles.default.border} p-4`}>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-yellow-100 to-yellow-200 rounded-xl flex items-center justify-center">
                <Trophy className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-800">{progressData.achievements_count || 0}</h3>
                <p className="text-sm text-gray-600">Achievements</p>
              </div>
            </div>
          </div>
        </div>

        {/* Settings Section */}
        <div className={`${cardStyles.default.background} ${cardStyles.default.rounded} ${cardStyles.default.shadow} ${cardStyles.default.border} ${cardStyles.default.padding}`}>
          <h3 className="text-xl font-bold text-gray-800 mb-4">Settings</h3>
          <div className="space-y-2">
            <button 
              onClick={() => setNotification('Notifications settings coming soon!')}
              className="w-full flex items-center justify-between p-4 hover:bg-peach-50 rounded-xl transition-colors group"
            >
              <div className="flex items-center gap-3">
                <Bell className="w-5 h-5 text-gray-600 group-hover:text-orange-600 transition-colors" />
                <span className="font-medium text-gray-700">Notifications</span>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-orange-600 transition-colors" />
            </button>
            
            <button 
              onClick={() => setNotification('Privacy settings coming soon!')}
              className="w-full flex items-center justify-between p-4 hover:bg-peach-50 rounded-xl transition-colors group"
            >
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-gray-600 group-hover:text-orange-600 transition-colors" />
                <span className="font-medium text-gray-700">Privacy & Security</span>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-orange-600 transition-colors" />
            </button>
            
            <button className="w-full flex items-center justify-between p-4 hover:bg-peach-50 rounded-xl transition-colors group">
              <div className="flex items-center gap-3">
                <HelpCircle className="w-5 h-5 text-gray-600 group-hover:text-orange-600 transition-colors" />
                <span className="font-medium text-gray-700">Help & Support</span>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-orange-600 transition-colors" />
            </button>
            
            {!profileData.is_premium && (
              <button 
                onClick={upgradeToPremium}
                className={`w-full flex items-center justify-between p-4 bg-gradient-to-r ${gradients.button} rounded-xl transition-all hover:shadow-lg group`}
              >
                <div className="flex items-center gap-3">
                  <Crown className="w-5 h-5 text-white" />
                  <span className="font-semibold text-white">Upgrade to Premium</span>
                </div>
                <ChevronRight className="w-5 h-5 text-white" />
              </button>
            )}
            
            <button 
              onClick={handleLogout}
              className="w-full flex items-center justify-between p-4 hover:bg-red-50 rounded-xl transition-colors group"
            >
              <div className="flex items-center gap-3">
                <LogOut className="w-5 h-5 text-red-500" />
                <span className="font-medium text-red-600">Sign Out</span>
              </div>
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ProfilePage;